<template>
  <router-view/>
  <!-- Global: uma ligação pode chegar sem contato e sem atendimento aberto, então
       não há chat onde ancorar o card. -->
  <LigacaoOverlay/>
  <!-- NovaVersaoAviso desligado: em aba sem service worker no controle (Ctrl+F5,
       primeira visita) o "Atualizar" não recarrega e o aviso fica preso. Volta
       quando o clique for corrigido. -->
</template>

<script>
import { useUiStore } from '@/stores/app/uiStore';
import { useChatStore } from '@/stores/app/chatStore';
import { mapWritableState } from 'pinia';
import { watch } from 'vue';
import { useInternalChatStore } from '@/stores/app/chatInternoStore';
import { branding } from '@/config/branding';
import { applyFavicon } from '@/utils/applyFavicon';
import { applyPwaManifest } from '@/utils/applyPwaManifest';
import LigacaoOverlay from '@/components/ligacoes/LigacaoOverlay.vue';

export default {
  name: 'MainView',
  components: { LigacaoOverlay },
  computed: {
    ...mapWritableState(useUiStore, ['theme'])
  },
  watch: {
    theme(newVal) {
      this.updateDOM(newVal);
    },
    
    $route() {
      this.updateDOM(this.theme);
    }
  },
  mounted() {
    this.applyBranding();
    this.initThemeStrategy();

    if (window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
        if (!localStorage.getItem('ui')) { // 'ui' é a chave padrão do persist da uiStore
          this.theme = e.matches ? 'dark' : 'light';
        }
      });
    }

    // LISTENER PWA: Recebe msg do service worker quando o sistema já estava aberto
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
          const { chatId: chatIdQueChegou, notificationType } = event.data;
          if (!chatIdQueChegou) return;

          if (notificationType === 'chat-interno') {
            this._abrirChatInterno(chatIdQueChegou);
          } else {
            useChatStore().selectChat(chatIdQueChegou);
          }
        }
      });
    }

    const urlParams = new URLSearchParams(window.location.search);
    const chatIdParam = urlParams.get('chatId');
    const notificationTypeParam = urlParams.get('notificationType');

    if (chatIdParam) {
      if (notificationTypeParam === 'chat-interno') {
        this._abrirChatInterno(chatIdParam);
      } else {
        const store = useChatStore();
        if (store.chatList.some(c => c.id === chatIdParam)) {
          store.selectChat(chatIdParam);
        } else {
          const unwatch = watch(() => store.chatList.length, () => {
            if (store.chatList.some(c => c.id === chatIdParam)) {
              store.selectChat(chatIdParam);
              unwatch();
            }
          });
        }
      }
    }
  },
  methods: {
    async _abrirChatInterno(chatId) {
      const internalStore = useInternalChatStore();
      const uiStore = useUiStore();
      const chatStore = useChatStore();

        // 1. Garante que os chats estão carregados
      if (!internalStore.initialized) {
        await internalStore.fetchChats();
      }

      // 2. Ativa o modo chat interno (navega para a seção)
      uiStore.setActiveTab('ChatEquipe');
      chatStore.setChatContext('team');
      internalStore.setInternalChatMode(true);

      // 3. Tenta encontrar e abrir o chat imediatamente
      const chat = internalStore.getChatById(chatId);
      if (chat) {
        internalStore.setActiveChat(chat);
        return;
      }

      // 4. Se não encontrou ainda, aguarda o allChats carregar
      const unwatch = watch(
        () => internalStore.allChats.length,
        () => {
          const chatEncontrado = internalStore.getChatById(chatId);
          if (chatEncontrado) {
            internalStore.setActiveChat(chatEncontrado);
            unwatch();
          }
        }
      );
    },
    
    /**
     * Aplica a marca no que está FORA da árvore do Vue: favicon, meta tags,
     * título da aba e manifesto do PWA.
     *
     * Tudo isso nasce fixo na Whiz em `index.html` e em `vite.config.ts`, e
     * precisa ser reescrito aqui porque a imagem de build é uma só: a marca só
     * se conhece em execução, pelo `window.configs.COMPANY` que o contêiner
     * grava. Sem isto, ProsaZap e Whiz Farma herdavam o título, a cor e o nome
     * de atalho da Whiz.
     */
    applyBranding() {
      applyFavicon(branding.favicon);
      applyPwaManifest(branding);

      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', branding.themeColor);
      }

      // Título: quem manda é o router, que o define por rota (admin × cliente)
      // a cada `beforeEach`. Aqui só cobrimos a janela ANTES da primeira
      // navegação resolver — daí o guard pelo valor de `index.html`, que é
      // fixo em "Whiz". Sem ele, este `mounted` poderia correr depois do
      // primeiro `beforeEach` e apagar o título por rota que ele acabou de pôr.
      if (document.title.trim() === 'Whiz') {
        document.title = branding.companyName;
      }

      const descricaoMeta = document.querySelector('meta[name="description"]');
      if (descricaoMeta) {
        descricaoMeta.setAttribute('content', `${branding.companyName} - Plataforma de atendimento`);
      }

      const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
      if (appleTitle) {
        appleTitle.setAttribute('content', branding.shortName);
      }
    },

    initThemeStrategy() {

      const storedTheme = localStorage.getItem('user-theme'); // Caso legado
      // const piniaStored = this.theme; // Estado atual do Pinia

      if (!storedTheme && !localStorage.getItem('ui')) {
        const systemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.theme = systemDark ? 'dark' : 'light';
      }
      
      this.updateDOM(this.theme);
    },

    updateDOM(currentTheme) {
      const isDark = currentTheme === 'dark';

      if (isDark) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }

      document.body.setAttribute('data-bs-theme', currentTheme);
      document.body.setAttribute('data-company', branding.key);

      localStorage.setItem('user-theme', currentTheme);
    }
  }
}
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

body {
  transition: background-color 0.3s ease, color 0.3s ease;
}

nav {
  padding: 30px;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}
</style>